import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { rememberMicrosoftReturn, rememberReturnTenant } from "./lib/auth-return-target.js";
import {
  api,
  ApiError,
  asRecord,
  normalizeList,
  readString,
  setCsrfToken,
  type JsonRecord,
} from "./lib/api.js";

export interface SessionUser {
  id: string;
  name: string;
  email: string;
  role: string;
}

export interface SessionTenant {
  id: string;
  name: string;
  slug?: string;
  kind?: string;
}

export interface Session {
  user: SessionUser;
  tenants: SessionTenant[];
  activeTenant: SessionTenant;
  csrfToken: string;
  features: Record<string, boolean>;
}

function normalizeTenant(value: unknown): SessionTenant {
  const tenant = asRecord(value);
  return {
    id: readString(tenant, "id", "tenantId", "tenant_id"),
    name: readString(tenant, "name", "displayName", "display_name") || "ETAIN Mandant",
    slug: readString(tenant, "slug") || undefined,
    kind: readString(tenant, "kind", "type") || undefined,
  };
}

export function normalizeSession(payload: unknown): Session {
  const outer = asRecord(payload);
  const source = Object.keys(asRecord(outer.session)).length ? asRecord(outer.session) : outer;
  const user = asRecord(source.user);
  const tenants = normalizeList(source.tenants)
    .map(normalizeTenant)
    .filter((tenant) => tenant.id);
  const activeValue = source.activeTenant ?? source.active_tenant;
  let activeTenant = normalizeTenant(activeValue);
  if (!activeTenant.id) {
    const activeId = readString(source, "activeTenantId", "active_tenant_id");
    activeTenant =
      tenants.find((tenant) => tenant.id === activeId) ?? tenants[0] ?? normalizeTenant({});
  }
  const rawFeatures = asRecord(source.features);
  const features = Object.fromEntries(
    Object.entries(rawFeatures).map(([key, value]) => [key, value === true || value === "true"]),
  );

  return {
    user: {
      id: readString(user, "id", "userId", "user_id"),
      name:
        readString(user, "name", "displayName", "display_name", "fullName", "full_name") ||
        readString(user, "email") ||
        "ETAIN Nutzer",
      email: readString(user, "email"),
      role: readString(user, "role", "roleName", "role_name") || "Nutzer",
    },
    tenants,
    activeTenant,
    csrfToken: readString(source, "csrfToken", "csrf_token"),
    features,
  };
}

async function loadSession(signal?: AbortSignal): Promise<Session | null> {
  try {
    const payload = await api.get<JsonRecord>("/auth/session", signal);
    return normalizeSession(payload);
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null;
    throw error;
  }
}

export function useSession() {
  const queryClient = useQueryClient();
  const previousAccess = useRef<string | undefined>(undefined);
  const query = useQuery({
    queryKey: ["session"],
    queryFn: ({ signal }) => loadSession(signal),
    staleTime: 30_000,
    refetchInterval: (currentQuery) => (currentQuery.state.data ? 3_000 : false),
    refetchIntervalInBackground: true,
    retry: (count, error) => !(error instanceof ApiError && error.status === 401) && count < 2,
  });

  const accessSignature = query.data
    ? JSON.stringify([query.data.user.id, query.data.activeTenant.id, query.data.features])
    : "anonymous";
  useEffect(() => {
    if (
      previousAccess.current &&
      previousAccess.current !== "anonymous" &&
      previousAccess.current !== accessSignature
    ) {
      void queryClient.cancelQueries({ predicate: (item) => item.queryKey[0] !== "session" });
      queryClient.removeQueries({ predicate: (item) => item.queryKey[0] !== "session" });
    }
    previousAccess.current = accessSignature;
  }, [accessSignature, queryClient]);
  useEffect(() => {
    setCsrfToken(query.data?.csrfToken);
    if (query.data?.activeTenant.id) rememberReturnTenant(query.data.activeTenant.id);
  }, [query.data?.csrfToken, query.data?.activeTenant.id]);

  return query;
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (credentials: { email: string; password: string }) => {
      const payload = await api.post<JsonRecord>("/auth/login", credentials);
      return normalizeSession(payload);
    },
    onSuccess: (session) => {
      setCsrfToken(session.csrfToken);
      queryClient.setQueryData(["session"], session);
    },
  });
}

export function useLearnerLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (credentials: { email: string; password: string }) => {
      const payload = await api.post<JsonRecord>("/auth/learner-login", credentials);
      return normalizeSession(payload);
    },
    onSuccess: (session) => {
      setCsrfToken(session.csrfToken);
      queryClient.setQueryData(["session"], session);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<void>("/auth/logout"),
    onSuccess: () => clearAuthenticatedSession(queryClient),
  });
}

export function clearAuthenticatedSession(queryClient: QueryClient) {
  rememberReturnTenant();
  rememberMicrosoftReturn();
  setCsrfToken();
  queryClient.setQueryData(["session"], null);
  queryClient.removeQueries({
    predicate: (query) => query.queryKey[0] !== "session",
  });
}

export function useSwitchTenant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tenantId: string) => api.post<JsonRecord>("/auth/switch-tenant", { tenantId }),
    onSuccess: (payload) => {
      const session = normalizeSession(payload);
      if (session.user.id) queryClient.setQueryData(["session"], session);
      else queryClient.invalidateQueries();
    },
  });
}
